import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs/operators';
import { EstimacionService } from '../services/estimacion.service';
import { AuthService } from '../auth/auth.service';
import { Estimation } from '../models/estimacion.model';
import { User } from '../models/user.model';
import { GlobalModalService } from '../shared/global-modal/global-modal.service';
import { Router } from '@angular/router';
import { LoggerService } from '../services/logger.service';
import { UserService } from '../services/user.service';
import { Task } from '../models/task.model';
import { Requirement } from '../models/requirement.model';
import { EstimationVersion } from '../models/estimation-version.model';
import { EstimationWarningsService } from '../services/estimation-warnings.service';
import {
  EstimationWarningCode,
  PERMISSION_VIEW_DIAGNOSTICS,
  EstimationCalibrationSummary,
  EstimationSelfCheck
} from '../models/estimation-v3.types';
import { ExportJobTracker } from '../export/services/export-job-tracker.service';

@Component({
  selector: 'app-estimation',
  templateUrl: './estimaciones.component.html',
  styleUrls: ['./estimaciones.component.scss']
})
export class EstimacionComponent implements OnInit, OnDestroy {
  selectedEstimation: Estimation = new Estimation();
  isEditing: boolean = false;
  estimationSaved = false;
  showModal = false;
  
  isSidebarOpen: boolean = false;
  isPromptOpen: boolean = false; // REFACTOR: Usamos Slide-Over
  warningsPanelOpen: boolean = false;
  isExportPanelOpen: boolean = false;

  // NUEVO: Variables de estado para el Slide-over de Edición de Tarea
  isTaskEditSlideOverOpen: boolean = false;
  editingTaskOriginalRef: Task | null = null;
  editingTaskCopy: Task | null = null;
  
  loading: boolean = false;
  isSavingDraft: boolean = false; 
  streamingText: string = '';
  
  user: User | any;
  totalHoras: number = 0;

  // Variables calculadas y seguras para los Tokens
  tokensTotales: number = 0;
  tokensConsumidos: number = 0;
  tokensRestantes: number = 0;
  lastExecutionTokens: number = 0;

  // ==========================================
  // ESTADO DE LA MÁQUINA DEL TIEMPO & DIFF
  // ==========================================
  historicalVersions: EstimationVersion[] = [];
  viewingVersionId: string = 'actual';
  draftRequirementsBackup: Requirement[] | null = null;
  draftPromptBackup: string | null = null;

  isDiffMode: boolean = false; 
  diffRequirements: any[] = [];
  diffPastTotal: number = 0;
  diffCurrentTotal: number = 0;
  hasDiffChanges: boolean = true; 

  get isHistoricalView(): boolean {
    return this.viewingVersionId !== 'actual';
  }

  getViewingVersionNumber(): number {
    const v = this.historicalVersions.find(v => v.id === this.viewingVersionId);
    return v ? v.version : 0;
  }

  // ==========================================
  // SEGURIDAD RBAC (Control por Permisos Centralizado)
  // ==========================================
  get canModifyEstimations(): boolean {
    if (!this.user) return false;
    
    // FIX ARQUITECTURA: Delegamos la lógica al AuthService, exactamente 
    // igual que hace la directiva [appHasPermission]. 
    // Si la seguridad de tu app requiere un contexto (ej. el ID de un proyecto), 
    // se pasaría como segundo parámetro: this.authService.hasPermission(..., context);
    return this.authService.hasPermission('estimation:modify');
  }
  // ==========================================

  get canExportEstimations(): boolean {
    if (!this.user) return false;
    return this.authService.hasPermission('estimation:export');
  }

  constructor(
    private estimacionService: EstimacionService,
    private globalModalService: GlobalModalService,
    private userService: UserService,
    public authService: AuthService,
    private router: Router,
    private logger: LoggerService,
    private cdRef: ChangeDetectorRef,
    private warningsService: EstimationWarningsService,
    private snackBar: MatSnackBar,
    private exportJobTracker: ExportJobTracker
  ) {}

  ngOnInit(): void {
    this.logger.info("EstimacionComponent.ngOnInit.");
    const savedEstimation = sessionStorage.getItem('selectedEstimation');

    if (savedEstimation) {
      this.selectedEstimation = JSON.parse(savedEstimation);
      this.isEditing = true;
      this.estimationSaved = true;
      this.isPromptOpen = false; // Aseguramos que inicie cerrado
      if (this.selectedEstimation.id) {
        this.loadVersions(this.selectedEstimation.id);
      }
    } else {
      this.newEstimation();
    }
    this.calcularTotalHoras();
    this.getUser();
  }

  ngOnDestroy(): void {
    sessionStorage.removeItem('selectedEstimation');
  }

  get hasExecutions(): boolean {
    let project = (this.selectedEstimation as any)?.project || (this.selectedEstimation as any)?.Project;
    
    if (!project) {
      const savedProject = sessionStorage.getItem('selectedProject');
      if (savedProject) {
        try {
          project = JSON.parse(savedProject);
        } catch(e) {}
      }
    }

    const executions = project?.taskexecutions || project?.TaskExecutions;
    return executions && executions.length > 0;
  }

  /**
   * Devuelve el primer código de warning bloqueante presente en la estimación
   * actual, o null si no hay ninguno. Usado para mostrar el pill de bloqueo.
   */
  get blockingWarningCode(): EstimationWarningCode | null {
    const warnings = this.selectedEstimation?.metadata?.warnings;
    const first = this.warningsService.getFirstBlocking(warnings);
    return first ? first.code : null;
  }

  /**
   * Tooltip explicativo del pill de bloqueo. Usa el label legible
   * del catálogo cerrado.
   */
  get blockingWarningTooltip(): string {
    const code = this.blockingWarningCode;
    if (!code) return '';
    const label = this.warningsService.getLabel(code);
    return `Motivo: ${label}. Click para ver detalle.`;
  }

  toggleSidebar() { this.isSidebarOpen = !this.isSidebarOpen; }
  
  // REFACTOR: Métodos de control del Slide-over
  togglePrompt() { this.isPromptOpen = !this.isPromptOpen; }
  closePrompt() { this.isPromptOpen = false; }

  // ==========================================================
  // NUEVO: Métodos de control del Slide-over de Tareas
  // ==========================================================
  openEditTaskSlideOver(task: Task): void {
    this.editingTaskOriginalRef = task;
    this.editingTaskCopy = JSON.parse(JSON.stringify(task)); // Deep copy para no ensuciar el grid si cancela
    this.isTaskEditSlideOverOpen = true;
  }

  closeEditTaskSlideOver(): void {
    this.isTaskEditSlideOverOpen = false;
    this.editingTaskOriginalRef = null;
    this.editingTaskCopy = null;
  }

  saveTaskEdit(): void {
    if (this.editingTaskOriginalRef && this.editingTaskCopy) {
      // 1. Respaldamos para poder hacer Undo
      this.backupTask(this.editingTaskOriginalRef);

      // 2. Volcamos solo propiedades modificables
      this.editingTaskOriginalRef.description = this.editingTaskCopy.description;
      this.editingTaskOriginalRef.type = this.editingTaskCopy.type;
      this.editingTaskOriginalRef.subsystem = this.editingTaskCopy.subsystem;
      this.editingTaskOriginalRef.technology = this.editingTaskCopy.technology;
      this.editingTaskOriginalRef.profile = this.editingTaskCopy.profile;
      this.editingTaskOriginalRef.estimatetime = this.editingTaskCopy.estimatetime;

      // 3. Disparamos el trigger que recalcula todo y cambia el badge a "IA (Modificada)"
      this.onTaskEdit(this.editingTaskOriginalRef);
    }
    this.closeEditTaskSlideOver();
  }
  
  // ==========================================================================
  // LÓGICA DE VIAJE EN EL TIEMPO & DIFF VIEW
  // ==========================================================================

  loadVersions(estimationId: number | string): void {
    this.estimacionService.getEstimationVersions(estimationId.toString()).subscribe(
      (versions) => { this.historicalVersions = versions; },
      (err) => this.logger.error("Error cargando histórico", err)
    );
  }

  onVersionChange(event: any): void {
    const versionId = event.target.value;
    this.viewingVersionId = versionId;
    this.isDiffMode = false; 

    if (versionId === 'actual') {
      if (this.draftRequirementsBackup) {
        this.selectedEstimation.requirements = JSON.parse(JSON.stringify(this.draftRequirementsBackup));
        this.draftRequirementsBackup = null;
      }
      if (this.draftPromptBackup !== null) {
        this.selectedEstimation.prompt = this.draftPromptBackup;
        this.draftPromptBackup = null;
      }
    } else {
      if (!this.draftRequirementsBackup) {
        this.draftRequirementsBackup = JSON.parse(JSON.stringify(this.selectedEstimation.requirements || []));
      }
      if (this.draftPromptBackup === null) {
        this.draftPromptBackup = this.selectedEstimation.prompt || '';
      }

      const selectedVer = this.historicalVersions.find(v => v.id === versionId);
      if (selectedVer) {
        this.selectedEstimation.requirements = selectedVer.snapshot ? JSON.parse(JSON.stringify(selectedVer.snapshot)) : [];
        this.selectedEstimation.prompt = selectedVer.scope || '';
      }
    }
    this.calcularTotalHoras();
  }

  restoreVersion(): void {
    this.draftRequirementsBackup = null; 
    this.draftPromptBackup = null;
    this.viewingVersionId = 'actual';
    this.isDiffMode = false;
    this.logger.info("Versión restaurada localmente. Pendiente de Guardado.");
  }

  toggleDiff(): void {
    if (this.isDiffMode) {
      this.computeDiff();
    }
  }

  computeDiff(): void {
    if (!this.draftRequirementsBackup || !this.isHistoricalView) return;

    const past = this.selectedEstimation.requirements || [];
    const current = this.draftRequirementsBackup || [];
    let diffResult: any[] = [];
    let pTotal = 0;
    let cTotal = 0;

    current.forEach(currReq => {
      let pastReq = past.find(p => p.id ? p.id === currReq.id : p.description === currReq.description);
      let diffReq = JSON.parse(JSON.stringify(currReq));

      if (!pastReq) {
        diffReq._diffStatus = 'added';
        diffReq.tasks.forEach((t: any) => { t._diffStatus = 'added'; cTotal += Number(t.estimatetime); });
      } else {
        diffReq._diffStatus = 'unchanged';
        let reqTasks: any[] = [];

        currReq.tasks.forEach(currTask => {
           let pastTask = pastReq!.tasks.find(pt => pt.id ? pt.id === currTask.id : pt.description === currTask.description);
           let diffTask = JSON.parse(JSON.stringify(currTask));
           cTotal += Number(currTask.estimatetime);

           if (!pastTask) {
             diffTask._diffStatus = 'added'; 
           } else {
             pTotal += Number(pastTask.estimatetime); 
             let isMod = false;
             
             if (Number(pastTask.estimatetime) !== Number(currTask.estimatetime)) {
               diffTask._oldHours = pastTask.estimatetime; isMod = true;
             }
             if ((pastTask.type || '') !== (currTask.type || '')) {
               diffTask._oldType = pastTask.type; isMod = true;
             }
             if ((pastTask.subsystem || '') !== (currTask.subsystem || '')) {
               diffTask._oldSubsystem = pastTask.subsystem; isMod = true;
             }
             if ((pastTask.technology || '') !== (currTask.technology || '')) {
               diffTask._oldTechnology = pastTask.technology; isMod = true;
             }
             if ((pastTask.profile || '') !== (currTask.profile || '')) {
               diffTask._oldProfile = pastTask.profile; isMod = true;
             }
             if ((pastTask.description || '') !== (currTask.description || '')) {
               diffTask._oldDescription = pastTask.description; isMod = true;
             }

             if (isMod) {
               diffTask._diffStatus = 'modified'; 
             } else {
               diffTask._diffStatus = 'unchanged';
             }
           }
           reqTasks.push(diffTask);
        });

        pastReq.tasks.forEach(pastTask => {
           let currTask = currReq.tasks.find(ct => ct.id ? ct.id === pastTask.id : ct.description === pastTask.description);
           if (!currTask) {
             let diffTask = JSON.parse(JSON.stringify(pastTask));
             diffTask._diffStatus = 'removed'; 
             pTotal += Number(pastTask.estimatetime);
             reqTasks.push(diffTask);
           }
        });
        diffReq.tasks = reqTasks;
      }
      diffResult.push(diffReq);
    });

    past.forEach(pastReq => {
      let currReq = current.find(cr => cr.id ? cr.id === pastReq.id : cr.description === pastReq.description);
      if (!currReq) {
        let diffReq = JSON.parse(JSON.stringify(pastReq));
        diffReq._diffStatus = 'removed';
        diffReq.tasks.forEach((t: any) => { t._diffStatus = 'removed'; pTotal += Number(t.estimatetime); });
        diffResult.push(diffReq);
      }
    });

    this.diffRequirements = diffResult;
    this.diffPastTotal = pTotal;
    this.diffCurrentTotal = cTotal;

    this.hasDiffChanges = diffResult.some(req => 
        req._diffStatus !== 'unchanged' || 
        req.tasks.some((t: any) => t._diffStatus !== 'unchanged')
    );
  }

  // ==========================================================================
  // LÓGICA DE EDICIÓN Y DATA PROVENANCE
  // ==========================================================================

  backupTask(task: Task): void {
    if (!task._original && (task.source === 'IA' || task.source === 'Histórica' || task.source === 'Historica')) {
      task._original = { ...task }; 
    }
  }

  onTaskEdit(task: Task): void {
    task._isModified = true;
    if (task.source === 'IA') { task.source = 'IA (Modificada)'; } 
    else if (task.source === 'Histórica' || task.source === 'Historica') { task.source = 'Histórica (Modificada)'; }
    this.calcularTotalHoras();
  }

  undoTaskEdit(task: Task): void {
    if (task._original) {
      Object.assign(task, task._original); 
      delete task._original;               
      delete task._isModified;             
      this.calcularTotalHoras();
    }
  }

  addTask(req: Requirement): void {
    if (!req.tasks) req.tasks = [];
    const newTask = new Task();
    newTask.description = 'Nueva Tarea';
    newTask.type = 'DESARROLLO';
    newTask.subsystem = 'General';
    newTask.technology = 'Varios';
    newTask.profile = 'Developer';
    newTask.estimatetime = 0;
    newTask.source = 'Manual';
    req.tasks.push(newTask);
    this.calcularTotalHoras();
  }

  addRequirement(): void {
    if (!this.selectedEstimation.requirements) this.selectedEstimation.requirements = [];
    const newReq = new Requirement();
    newReq.description = 'NUEVO BLOQUE DE REQUISITOS';
    const newTask = new Task();
    newTask.description = 'Definición de tarea inicial';
    newTask.type = 'ANÁLISIS';
    newTask.subsystem = 'General';
    newTask.technology = 'N/A';
    newTask.profile = 'Business Analyst';
    newTask.estimatetime = 0;
    newTask.source = 'Manual';
    newReq.tasks = [newTask];
    this.selectedEstimation.requirements.push(newReq);
    this.calcularTotalHoras();
  }

  deleteTask(req: Requirement, index: number): void {
    const task = req.tasks[index];

    // Confirmación reforzada para tareas preventivas (execution_only).
    if (task?.source_subtype === 'execution_only') {
      this.globalModalService.confirm(
        'Borrar tarea preventiva',
        'Esta tarea es preventiva basada en histórico de proyectos análogos. ¿Seguro que la borras?'
      ).subscribe(confirmed => {
        if (confirmed) {
          req.tasks.splice(index, 1);
          this.calcularTotalHoras();
        }
      });
      return;
    }
    req.tasks.splice(index, 1);
    this.calcularTotalHoras();
  }

  saveManualChanges(): void {
    this.isSavingDraft = true; 
    this.estimacionService.saveEstimation(this.selectedEstimation)
      .pipe(finalize(() => { this.isSavingDraft = false; }))
      .subscribe(
        (data) => { this.loadEstimation(data.id, true); },
        (err) => { this.globalModalService.showError('Error', err.message || 'No se pudieron guardar los cambios.'); }
      );
  }

  calculateEstimation(): void {
    if (this.hasExecutions) return; 
    this.loading = true; 
    this.streamingText = '';
    this.lastExecutionTokens = 0;

    this.estimacionService.streamRAGPT(this.selectedEstimation, (chunkText) => {
      this.streamingText = chunkText;
      this.cdRef.detectChanges(); 
    }).then((parsedResults) => {
      if (parsedResults && parsedResults.requirements) {
        this.selectedEstimation.requirements = parsedResults.requirements;
      }

      // === NUEVO: asignar bloques v3 si vienen en la respuesta ===
      if (parsedResults && parsedResults.metadata) {
        this.selectedEstimation.metadata = parsedResults.metadata;
      }
      if (parsedResults && parsedResults.metadata_original) {
        this.selectedEstimation.metadata_original = parsedResults.metadata_original;
      }
      if (parsedResults && parsedResults.rollup) {
        this.selectedEstimation.rollup = parsedResults.rollup;
      }
      this.autoOpenWarningsIfBlocking();
      // === FIN NUEVO ===

      // CAPTURAR COSTE DE LA ÚLTIMA EJECUCIÓN
      if (parsedResults._metadata && parsedResults._metadata.tokens) {
        this.lastExecutionTokens = parsedResults._metadata.tokens;
      }
      
      this.isPromptOpen = false; // REFACTOR: Cerramos el panel lateral tras éxito
      this.userService.updateUser(null);
      this.estimacionService.saveEstimation(this.selectedEstimation)
        .pipe(finalize(() => { this.loading = false; }))
        .subscribe((data) => { this.loadEstimation(data.id, true); }, () => {});
    }).catch((err) => {
      this.loading = false;
      this.globalModalService.showError('Error', err.message || 'Error de conexión');
    });
  }

  loadEstimation(id: number, keepTokens: boolean = false): void {
    this.estimacionService.getEstimationById(id.toString()).subscribe((data) => {
      this.selectedEstimation = data;
      this.calcularTotalHoras();
      this.autoOpenWarningsIfBlocking();
      if (!keepTokens) this.lastExecutionTokens = 0;
      this.loadVersions(data.id);
    });
  }

  getUser() {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.user = currentUser;
      this.calcularTokens();
    }
    this.userService.user$.subscribe(user => {
      if (user) {
        this.user = user;
        this.calcularTokens();
      }
    });
  }

  // Método seguro para la matemática de Tokens (previene NaN)
  calcularTokens(): void {
    this.tokensTotales = Number(this.user?.totaltokens || 0);
    this.tokensConsumidos = Number(this.user?.consumedtokens || 0);
    this.tokensRestantes = this.tokensTotales - this.tokensConsumidos;
  }

  deleteEstimation(): void {
    if (this.selectedEstimation?.id) {
        this.globalModalService.confirm('Borrar Registro', '¿Confirmas la eliminación definitiva?').subscribe(confirmed => {
            if (confirmed) {
                this.estimacionService.deleteEstimation(this.selectedEstimation!.id?.toString()).subscribe(() => {
                    this.router.navigate(['/app/home']);
                });
            }
        });
    }
  }

  volverProyecto(): void { this.router.navigate(['/app/proyecto']); }

  newEstimation(): void {
    this.selectedEstimation = new Estimation();
    this.selectedEstimation.requirements = [];
    this.isEditing = true;
    this.estimationSaved = false;
    this.lastExecutionTokens = 0;
    this.isPromptOpen = true; // REFACTOR: Si es nueva, abrimos para que escriba
    this.historicalVersions = [];
    this.viewingVersionId = 'actual';
    this.isDiffMode = false;
    this.warningsPanelOpen = false;
  }

  openModal() {
    if (this.isEditing) {
      this.globalModalService.confirm('Nueva Estimación', 'Hay cambios sin guardar. ¿Crear nueva estimación?').subscribe((confirmed) => {
        if(confirmed) this.initializeNewEstimation();
      });
    } else { this.initializeNewEstimation(); }
    this.estimationSaved = false;
    this.showModal = true;
  }

  initializeNewEstimation(): void {
    this.selectedEstimation = new Estimation();
    if (!this.selectedEstimation.requirements) { this.selectedEstimation.requirements = []; }
    this.isEditing = true;
    this.lastExecutionTokens = 0;
    this.isPromptOpen = true;
    this.historicalVersions = [];
    this.viewingVersionId = 'actual';
    this.isDiffMode = false;
    this.warningsPanelOpen = false;
  }

  closeModal() { this.showModal = false; }

  calcularTotalHoras(): void {
    this.totalHoras = this.selectedEstimation.requirements.reduce((total: any, req: any) => {
      return total + req.tasks.reduce((sub: any, t: any) => sub + Number(t.estimatetime), 0);
    }, 0);
  }

  /**
   * Toggle del panel inline de warnings (entre banner y unified-command-bar).
   * No requiere abrir el drawer lateral — el panel es independiente.
   */
  onOpenWarnings(): void {
    this.warningsPanelOpen = !this.warningsPanelOpen;
  }

  /** Cierra el panel de warnings (callback del [×] del panel). */
  onCloseWarnings(): void {
    this.warningsPanelOpen = false;
  }

  /**
   * Abre automáticamente el panel de warnings si hay alguno bloqueante.
   * Se llama tras cargar/sintetizar una estimación.
   */
  private autoOpenWarningsIfBlocking(): void {
    const warnings = this.selectedEstimation?.metadata?.warnings;
    if (this.warningsService.isBlocking(warnings)) {
      this.warningsPanelOpen = true;
    }
  }

  openExportPanel(): void {
    if (!this.estimationSaved) return;
    if (this.isHistoricalView || this.isDiffMode) return;
    if (this.loading) return;
    this.isExportPanelOpen = true;
  }

  onExportPanelClosed(): void { this.isExportPanelOpen = false; }

  onExportSuccess(event: { exporterId: string; filename?: string }): void {
    const msg = event.filename
      ? `>_ EXPORT OK · ${event.filename}`
      : `>_ EXPORT OK · destino ${event.exporterId.toUpperCase()}`;
    this.snackBar.open(msg, '✓', {
      duration: 4500,
      panelClass: ['cyber-toast'],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  /**
   * Async export ENCOLADO: el panel ya cerró. Arrancamos el tracker para que
   * abra el SSE y mantenga la Status Pill viva. Cuando termine, mostramos
   * el Toast resumen y el tracker olvida el job.
   */
  async onExportEnqueued(event: { jobId: string; exporterId: string; exporterLabel: string }): Promise<void> {
    const finalState = await this.exportJobTracker.track(
      event.jobId,
      event.exporterId,
      event.exporterLabel
    );

    // Toast según estado final.
    if (finalState.status === 'success') {
      const created = finalState.createdCount ?? 0;
      const url = finalState.externalUrl ? ` · ${finalState.externalUrl}` : '';
      this.snackBar.open(
        `>_ ${event.exporterLabel.toUpperCase()} OK · ${created} creadas${url}`,
        '✓',
        { duration: 5500, panelClass: ['cyber-toast'], horizontalPosition: 'center', verticalPosition: 'top' }
      );
    } else if (finalState.status === 'partial') {
      this.snackBar.open(
        `>_ ${event.exporterLabel.toUpperCase()} PARCIAL · ${finalState.createdCount ?? 0} OK, ${finalState.failedCount ?? 0} fallos`,
        '!',
        { duration: 6500, panelClass: ['cyber-toast'], horizontalPosition: 'center', verticalPosition: 'top' }
      );
    } else if (finalState.status === 'cancelled') {
      this.snackBar.open(
        `>_ ${event.exporterLabel.toUpperCase()} CANCELADO`,
        '',
        { duration: 3500, panelClass: ['cyber-toast'], horizontalPosition: 'center', verticalPosition: 'top' }
      );
    } else {
      // failed
      this.globalModalService.showError(
        'Exportación fallida',
        `El destino "${event.exporterLabel}" falló: ${finalState.error ?? 'error desconocido'}`
      );
    }

    this.exportJobTracker.forget(event.jobId);
  }

  onExportFailed(event: { exporterId: string; error: string }): void {
    this.globalModalService.showError(
      'Error de exportación',
      `No se pudo completar la exportación a "${event.exporterId}". ${event.error}`
    );
  }

  // ==========================================================================
  // PUNTO F — DRAWER EXTENDIDO (CALIBRACIÓN + DIAGNÓSTICO)
  // ==========================================================================

  /** True si el usuario puede ver el bloque de diagnóstico admin. */
  get canViewDiagnostics(): boolean { return this.authService.hasPermission(PERMISSION_VIEW_DIAGNOSTICS); }
  get calibrationSummary(): EstimationCalibrationSummary | undefined { return this.selectedEstimation?.metadata?.calibration_summary; }

  /** True si hay datos de calibración para mostrar la sección. */
  get hasCalibrationData(): boolean {
    const cs = this.calibrationSummary;
    if (!cs) return false;
    return (
      typeof cs.samples_used === 'number' ||
      typeof cs.median_calibration_ratio === 'number' ||
      typeof cs.phantom_tasks_detected === 'number' ||
      typeof cs.outliers_excluded === 'number'
    );
  }

  /**
   * Clase CSS para colorear el ratio mediano de calibración.
   * - rojo: ratio > 1.5 (subestimación severa histórica)
   * - amarillo: ratio > 1.2
   * - neutro: 0.95–1.20
   * - verde: ratio ≤ 0.95 (sobreestimación)
   */
  get calibrationRatioClass(): string {
    const r = this.calibrationSummary?.median_calibration_ratio;
    if (typeof r !== 'number') return '';
    if (r > 1.5) return 'ratio-danger';
    if (r > 1.2) return 'ratio-warning';
    if (r <= 0.95) return 'ratio-good';
    return 'ratio-neutral';
  }

  /** Estado del self_check v3, cada flag con label y valor booleano. */
  get selfCheckItems(): Array<{ key: string; label: string; value: boolean | undefined }> {
    const sc: EstimationSelfCheck | undefined = this.selectedEstimation?.metadata?.self_check;
    if (!sc) return [];
    return [
      { key: 'sdlc_present',                  label: 'SDLC presente',          value: sc.sdlc_present },
      { key: 'deduplication_applied',         label: 'Deduplicación aplicada', value: sc.deduplication_applied },
      { key: 'guardrails_respected',          label: 'Guardrails respetados',  value: sc.guardrails_respected },
      { key: 'calibration_applied_when_data', label: 'Calibración aplicada',   value: sc.calibration_applied_when_data },
      { key: 'rollup_consistent',             label: 'Rollup consistente',     value: sc.rollup_consistent },
      { key: 'all_tasks_have_signature',      label: 'Firmas presentes',       value: sc.all_tasks_have_signature }
    ];
  }

  /** True si hay cualquier dato de diagnóstico que mostrar (gate del bloque admin). */
  get hasDiagnosticsData(): boolean {
    const m = this.selectedEstimation?.metadata;
    if (!m) return false;
    return !!(
      m.prompt_version ||
      typeof m.ai_assistance_observed_factor === 'number' ||
      m.reasoning_trace ||
      m.self_check
    );
  }

  // ==========================================================================
  // SUB-PUNTO G.2 — Drawer reorganizado: getters auxiliares
  // ==========================================================================

  /** Tipo de proyecto v3 (etiqueta legible para drawer). */
  get projectTypeLabelLong(): string {
    const t = this.selectedEstimation?.metadata?.project_type_classified;
    if (!t) return '';
    const map: Record<string, string> = {
      NEW: 'Proyecto nuevo',
      EVOLUTIONARY: 'Evolutivo',
      RECURRING: 'Recurrente',
      MAINTENANCE: 'Mantenimiento',
      UNKNOWN: 'Sin clasificar'
    };
    return map[t] || '';
  }

  /** Confianza global v3 (etiqueta legible para drawer). */
  get confidenceLabelLong(): string {
    const c = this.selectedEstimation?.metadata?.overall_confidence;
    if (!c) return '';
    const map: Record<string, string> = {
      LOW: 'Confianza baja',
      MEDIUM: 'Confianza media',
      HIGH: 'Confianza alta'
    };
    return map[c] || '';
  }

  /** Clase CSS del semáforo de confianza para el drawer. */
  get confidenceClass(): string {
    const c = this.selectedEstimation?.metadata?.overall_confidence;
    if (!c) return '';
    return 'confidence-' + c.toLowerCase();
  }

  /** Etiqueta legible de la base de estimación para drawer. */
  get basisLabelLong(): string {
    const b = this.selectedEstimation?.metadata?.estimation_basis;
    if (!b) return '';
    const map: Record<string, string> = {
      industry_baseline_2026: 'Estándar industria 2026',
      historical_calibrated: 'Calibrado con histórico',
      mixed: 'Mixto'
    };
    return map[b] || '';
  }

  /** True si hay info de confianza/base para mostrar la sección del drawer. */
  get hasConfidenceAndBasisData(): boolean {
    const m = this.selectedEstimation?.metadata;
    return !!(m?.project_type_classified || m?.overall_confidence || m?.estimation_basis);
  }

  /** True si hay totales en rollup para mostrar la sección del drawer. */
  get hasTotalsData(): boolean {
    const r = this.selectedEstimation?.rollup;
    if (!r) return false;
    return (
      typeof r.total_p50_hours === 'number' ||
      typeof r.contingency_buffer_hours === 'number' ||
      typeof r.total_with_contingency_hours === 'number' ||
      typeof r.phantom_tasks_total_hours === 'number'
    );
  }

  /** True si hay warnings para mostrar la sección del drawer. */
  get hasWarningsInDrawer(): boolean {
    return (this.selectedEstimation?.metadata?.warnings?.length ?? 0) > 0;
  }

  /** Lista de warnings tal cual del metadata, o array vacío. */
  get drawerWarnings() {
    return this.selectedEstimation?.metadata?.warnings ?? [];
  }

  /** Etiqueta legible de un warning code (delegada al service). */
  getWarningLabel(code: string): string {
    return this.warningsService.getLabel(code as any);
  }

  /** Severidad CSS de un warning (blocking | risk | info). */
  getWarningSeverity(code: string): string {
    return this.warningsService.getSeverity(code as any);
  }

  // ==========================================================================
  // RECONCILIACIÓN DE ROLLUP (v3.1 — backend reconcile)
  // ==========================================================================

  /**
   * True si el backend tuvo que reconciliar el rollup en esta estimación.
   * Detectamos esto por la presencia de `metadata_original` (snapshot pre-reconciliación).
   */
  get hasRollupDiscrepancy(): boolean {
    return !!this.selectedEstimation?.metadata_original;
  }

  /** P50 declarado originalmente por el modelo (antes de reconciliación). */
  get originalP50(): number | undefined {
    // Truco: el rollup ORIGINAL no se guarda — solo el metadata original. Pero
    // podemos reconstruirlo: si hay metadata_original, sabemos que el rollup
    // actual es el saneado. El P50 original lo extraemos del warning details.
    const warnings = this.selectedEstimation?.metadata?.warnings ?? [];
    const w = warnings.find(x => x.code === 'ROLLUP_RECALCULATED');
    if (!w?.details) return undefined;
    const m = w.details.match(/declaró\s+(\d+(?:\.\d+)?)h\s+P50/i);
    return m ? Number(m[1]) : undefined;
  }

  /** P50 real (suma de tareas) — coincide con el rollup actual. */
  get realP50(): number | undefined {
    return this.selectedEstimation?.rollup?.total_p50_hours;
  }

  /** Diferencia absoluta en horas entre lo declarado por el modelo y la suma real. */
  get rollupDiff(): number | undefined {
    const orig = this.originalP50;
    const real = this.realP50;
    if (typeof orig !== 'number' || typeof real !== 'number') return undefined;
    return Math.abs(orig - real);
  }

  /** Clase CSS para el color de la diferencia (sutil: amarillo siempre, no es un error grave). */
  get rollupDiffClass(): string {
    const diff = this.rollupDiff;
    if (typeof diff !== 'number') return '';
    return 'value-warning';
  }
}